import { AUTH_REFRESH_TOKEN, AUTH_TOKEN } from '@/modules/Auth';
import { Center, Spinner, VStack, useColorMode } from '@chakra-ui/react';
import { colors } from '@/styles/theme/foundations/colors';
import { useLocalStorage } from '@/modules/Storage';
import { useAuthControllerIsAuthenticated } from '@/generated/auth/auth';
import { auth } from '@/modules/link';
import Axios from 'axios';
import { useRouter } from 'next/router';
import { useSearchParams } from 'next/navigation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAnalytics } from 'use-analytics';
import { useCookie } from '@/util/use-cookie';
import { CookieManager } from '../utils/cookie-manager';
import { useIntercom } from 'react-use-intercom';

export const AuthContext = React.createContext<{
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  setToken: React.Dispatch<React.SetStateAction<string | null>>;
  setRefreshToken: React.Dispatch<React.SetStateAction<string | null>>;
  signOut: () => void;
  redirectUrl: string | null;
}>({
  isAuthenticated: false,
  token: null,
  refreshToken: null,
  setToken: () => {
    throw new Error('setToken - you need to setToken to a func before using');
  },
  setRefreshToken: () => {
    throw new Error('setRefreshToken - you need to setToken to a func before using');
  },
  signOut: () => {
    throw new Error('signOut - you need to set signOut to a func before using');
  },
  redirectUrl: null,
});

function setAxiosAuthHeader(token: string | null) {
  Axios.defaults.headers.common = { Authorization: `Bearer ${token || ''}` };
}

export interface AuthProviderProps {
  token?: string;
  refreshToken?: string;
  isAuthenticated?: boolean;
}
export const AuthProvider = (props: React.PropsWithChildren<AuthProviderProps>) => {
  const router = useRouter();
  const { reset } = useAnalytics();
  const { shutdown } = useIntercom();
  const searchParams = useSearchParams();
  const redirectUrl = searchParams.get('auth-redirect');
  const [v2AuthToken, setV2AuthToken, removeV2AuthToken] = useCookie(
    CookieManager.getV2AuthTokenName(),
    CookieManager.getAttributes(),
  );
  const [v2RefreshToken, setV2RefreshToken, removeV2RefreshToken] = useCookie(
    CookieManager.getV2RefreshTokenName(),
    CookieManager.getAttributes(),
  );

  const { colorMode } = useColorMode();
  const [token, setToken] = useLocalStorage<string | null>(AUTH_TOKEN, props.token || null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [refreshToken, setRefreshToken] = useLocalStorage<string | null>(
    AUTH_REFRESH_TOKEN,
    props.refreshToken || null,
  );

  const signOut = useCallback(() => {
    void reset();
    // Shutting down the Intercom instance to prevent any further communication on logout
    shutdown();
    setToken(null);
    setRefreshToken(null);
    void router.push(auth.login);
  }, [reset, setRefreshToken, setToken, shutdown]);

  // Execute before mount, only once!
  useMemo(() => {
    setAxiosAuthHeader(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: isAuthenticatedResponse, error: authResponseFailure } = useAuthControllerIsAuthenticated({
    swr: { swrKey: token, enabled: !!token },
  });

  useEffect(() => {
    if (authResponseFailure) {
      signOut();
    }
  }, [authResponseFailure, signOut]);

  // Set token in Axios header and set isAuthenticated to true if token is present
  useEffect(() => {
    setAxiosAuthHeader(token);
    if (isAuthenticatedResponse) {
      setIsAuthenticated(true);
    }
  }, [token, isAuthenticatedResponse]);

  // Set cookies on token change
  useEffect(() => {
    token ? setV2AuthToken(token) : removeV2AuthToken();
    refreshToken ? setV2RefreshToken(refreshToken) : removeV2RefreshToken();
  }, [token, refreshToken, setV2AuthToken, setV2RefreshToken, removeV2AuthToken, removeV2RefreshToken]);

  // Redirect to login page if token or refreshToken is missing
  useEffect(() => {
    const isOnPublicPage = [
      router.pathname === auth.login,
      // initiate forgot password
      router.pathname === auth.forgotPassword,
      // reset password via email link
      router.pathname === `${auth.forgotPassword}/[resetToken]/[email]`,
      router.pathname.includes('/invitation'),
      router.pathname === auth.logout,
    ].some(Boolean);

    if ((!token || !refreshToken) && !isOnPublicPage) {
      router.replace(auth.login).catch((e: Error) => e);
    }
  }, [token, refreshToken, router]);

  // Redirect to the redirectUrl if user is authenticated
  useEffect(() => {
    if (v2AuthToken && v2RefreshToken && redirectUrl && isAuthenticated) {
      window.location.replace(redirectUrl);
    }
  }, [v2AuthToken, v2RefreshToken, redirectUrl, isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{ token, setToken, refreshToken, setRefreshToken, isAuthenticated, signOut, redirectUrl }}
    >
      {redirectUrl && isAuthenticated ? (
        <Center minH="100vh" as={VStack} bg={colors.background.global[colorMode]}>
          <Spinner />
        </Center>
      ) : (
        props?.children
      )}
    </AuthContext.Provider>
  );
};
